/**
 * PATH: apps/quote/src/components/builder/application-selector-modal/ApplicationPreviewPanel.tsx
 * PURPOSE: Right-side detail preview for a selected/hovered application
 */

import { Shield, Check } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { Application } from '@/lib/quoteApi'

interface ApplicationPreviewPanelProps {
  app: Application
  onSelect: (app: Application) => void
}

export default function ApplicationPreviewPanel({ app, onSelect }: ApplicationPreviewPanelProps) {
  return (
    <div className="w-80 border-l border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 overflow-y-auto">
      <div className="p-4">
        <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-1">
          {app.menu_item || app.name}
        </h3>
        {app.code && <p className="text-xs font-mono text-slate-500 mb-3">{app.code}</p>}
        {app.description && <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{app.description}</p>}

        {/* Classification */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Classification</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-slate-500">Area:</div>
            <div className="text-slate-900 dark:text-white">{app.area_type_display || '—'}</div>
            <div className="text-slate-500">Substrate:</div>
            <div className="text-slate-900 dark:text-white">{app.substrate_type_display || '—'}</div>
            <div className="text-slate-500">Exposure:</div>
            <div className="text-slate-900 dark:text-white">{app.exposure_type_display || '—'}</div>
          </div>
        </div>

        {/* Material Costs */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Material Costs</h4>
          <div className="space-y-2 text-sm">
            {app.primer_product && (
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Primer</span>
                <span className="font-medium">{formatCurrency(Number(app.primer_cost_per_m2 || 0))}/m²</span>
              </div>
            )}
            {app.basecoat_product && (
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Basecoat</span>
                <span className="font-medium">{formatCurrency(Number(app.basecoat_cost_per_m2 || 0))}/m²</span>
              </div>
            )}
            {app.topcoat_product && (
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Topcoat</span>
                <span className="font-medium">{formatCurrency(Number(app.topcoat_cost_per_m2 || 0))}/m²</span>
              </div>
            )}
            {Number(app.wastage_percent || 0) > 0 && (
              <div className="flex justify-between text-slate-500">
                <span>Wastage</span>
                <span>{(Number(app.wastage_percent) * 100).toFixed(0)}%</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700 font-semibold">
              <span>Total</span>
              <span className="text-indigo-600 dark:text-indigo-400">{formatCurrency(Number(app.total_cost_per_m2 || 0))}/m²</span>
            </div>
          </div>
        </div>

        {/* Productivity */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Productivity Rates</h4>
          <div className="space-y-2 text-sm">
            {app.productivity_simple && Number(app.productivity_simple) > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Simple areas</span>
                <span className="font-medium">{Number(app.productivity_simple).toFixed(0)} m²/day</span>
              </div>
            )}
            {app.productivity_standard && Number(app.productivity_standard) > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Standard</span>
                <span className="font-medium">{Number(app.productivity_standard).toFixed(0)} m²/day</span>
              </div>
            )}
            {app.productivity_complex && Number(app.productivity_complex) > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Complex</span>
                <span className="font-medium">{Number(app.productivity_complex).toFixed(0)} m²/day</span>
              </div>
            )}
          </div>
        </div>

        {/* Warranty */}
        {app.warranty_years && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Warranty</h4>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              <span className="text-lg font-semibold text-slate-900 dark:text-white">{app.warranty_years} Years</span>
            </div>
            {app.warranty_notes && <p className="text-xs text-slate-500 mt-1">{app.warranty_notes}</p>}
          </div>
        )}

        <button
          onClick={() => onSelect(app)}
          className="w-full mt-4 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" /> Apply This Application
        </button>
      </div>
    </div>
  )
}
